// @givmo/crm — one feed-sourced CRM record.
//
// The rollup, activity timeline, and organization-authored attachments for one feed record.
//
// ★ MONEY LAW: the rollup total is the SERVER's gross figure (D-CP-17) and every timeline amount is the
// server's own line, rendered verbatim. Nothing here re-sums the donation lines to check the total —
// the backend already tied both to the same ledger enumeration the §321 statement uses. No arithmetic.
//
// THE TIMELINE IS ORDERING, NOT DERIVATION. Donations and notes are interleaved newest-first by their
// server timestamps; the client decides only which row prints first. It never computes a value, never
// infers a status, and never invents an event that the server did not return.
//
// D-CP-32 — registered and pending identities are both attachment-capable on the contact-backed
// store. This screen offers controls to EVERY feed subject and special-cases none. A residual
// qualification refusal remains uniform, surfaces the server's message once, and stops; no client
// capability discriminator exists or is needed.
//
// NOT A RECEIPT: no receipt or deductibility framing appears anywhere in this feature.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Skeleton,
  StatCard,
  Textarea,
  TextField,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  ArrowLeft,
  CalendarDays,
  Coins,
  FileText,
  MessageSquare,
  Pencil,
  ShieldAlert,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import type { CrmModuleConfig } from './crm-config';
import type { CrmDonorDetailOut, CrmDonorNoteOut } from './wire-types';
import { crmDonorOptions, crmKeys } from './crm-queries';
import {
  addCrmDonorTag,
  createCrmDonorNote,
  deleteCrmDonorNote,
  removeCrmDonorTag,
  updateCrmDonorNote,
} from './crm-transport';
import {
  buildTimeline,
  displayCount,
  displayDate,
  displayMoney,
  displayText,
  isSliceUnavailable,
  isTerminalWriteRefusal,
  serverMessage,
} from './crm-decode';

const PREFIX = 'donor';

/**
 * Turn a failed tag/note write into one toast. A terminal qualification refusal, duplicate tag, or
 * dark route carries the server's own explanation and is final —
 * say exactly what the server said and stop. Anything else is a transport problem the caller can
 * genuinely retry, so it gets the retryable wording. Neither path re-arms the mutation.
 */
function writeFailureMessage(error: unknown, retryable: string): string {
  return isTerminalWriteRefusal(error) ? serverMessage(error, retryable) : retryable;
}

export function CrmDonorDetailPage({
  config,
  donorId,
}: {
  config: CrmModuleConfig;
  donorId: string;
}) {
  const queryClient = useQueryClient();

  const detailQuery = useQuery(crmDonorOptions(config.orgRef, donorId));

  const detail: CrmDonorDetailOut | null | undefined = detailQuery.data;
  const isPending = detailQuery.isPending;

  /** Re-read the record after any write so tags/notes reflect the server, never a local guess. */
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: crmKeys.donor(config.orgRef, donorId),
    });
  };

  if (isPending) {
    return (
      <PageShell config={config}>
        <Card className="p-24" testId={`${PREFIX}-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (detailQuery.isError || detail == null) {
    // A closed gate, a subject that never shared, and an unknown subject all resolve to
    // the SAME failure — deliberately no existence oracle. So this screen says one honest thing for
    // all of them rather than guessing which case it is.
    const unavailable = isSliceUnavailable(detailQuery.error);
    return (
      <PageShell config={config}>
        <EmptyState
          testId={`${PREFIX}-unavailable`}
          icon={<Icon icon={unavailable ? Users : ShieldAlert} size={20} aria-hidden />}
          title={unavailable ? 'Not available yet' : 'We couldn’t open that contact'}
          description={
            unavailable
              ? 'This part of the portal isn’t switched on for your organization yet.'
              : serverMessage(
                  detailQuery.error,
                  'This contact isn’t available in your organization’s records.',
                )
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void detailQuery.refetch()}
              testId={`${PREFIX}-retry`}
            >
              Try again
            </Button>
          }
        />
      </PageShell>
    );
  }

  const donor = detail.donor;

  return (
    <PageShell
      config={config}
      donorName={displayText(donor.name, config.vocab.anonymousFeedRecordLabel)}
    >
      {/* ── Rollup: every figure the SERVER's, verbatim. ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-16 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          testId={`${PREFIX}-tile-total`}
          label={config.vocab.amountTotalLabel}
          value={displayMoney(donor.total_donated)}
          icon={<Icon icon={Coins} size={18} aria-hidden />}
          sublabel="Before fees"
        />
        <StatCard
          testId={`${PREFIX}-tile-count`}
          label={config.vocab.activityCountLabel}
          value={displayCount(donor.donation_count)}
          icon={<Icon icon={FileText} size={18} aria-hidden />}
        />
        <StatCard
          testId={`${PREFIX}-tile-first`}
          label={config.vocab.firstActivityLabel}
          value={displayDate(donor.first_donation_date)}
          icon={<Icon icon={CalendarDays} size={18} aria-hidden />}
        />
        <StatCard
          testId={`${PREFIX}-tile-last`}
          label={config.vocab.lastActivityLabel}
          value={displayDate(donor.last_donation_date)}
          icon={<Icon icon={CalendarDays} size={18} aria-hidden />}
        />
      </div>

      <Card testId={`${PREFIX}-identity`}>
        <CardBody>
          <div className="flex flex-wrap items-center justify-between gap-12">
            <div className="flex flex-col">
              <span className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
                {displayText(donor.name, config.vocab.anonymousFeedRecordLabel)}
              </span>
              <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                {displayText(donor.email)}
              </span>
            </div>
            {donor.recurring === true ? (
              <Badge tone="success" dot testId={`${PREFIX}-recurring`}>
                {config.vocab.recurringDetailLabel}
              </Badge>
            ) : null}
          </div>
        </CardBody>
      </Card>

      <TagsPanel
        orgRef={config.orgRef}
        donorId={donorId}
        tags={donor.tags ?? []}
        onChanged={refresh}
      />

      <NotesPanel config={config} donorId={donorId} onChanged={refresh} />

      <TimelinePanel config={config} donorId={donorId} detail={detail} onChanged={refresh} />
    </PageShell>
  );
}

// ── Tags ──────────────────────────────────────────────────────────────────────────────────────────

function TagsPanel({
  orgRef,
  donorId,
  tags,
  onChanged,
}: {
  orgRef: string;
  donorId: string;
  tags: readonly string[];
  onChanged: () => void;
}) {
  const [label, setLabel] = useState('');

  const addTag = useMutation({
    // No retry: duplicate, qualification and dark-route refusals are terminal.
    retry: false,
    mutationFn: (value: string) => addCrmDonorTag(orgRef, donorId, { label: value }),
    onSuccess: () => {
      setLabel('');
      onChanged();
    },
    onError: (error) => {
      toast.error(writeFailureMessage(error, 'We couldn’t add that tag. Please try again.'));
    },
  });

  const removeTag = useMutation({
    retry: false,
    mutationFn: (value: string) => removeCrmDonorTag(orgRef, donorId, value),
    onSuccess: onChanged,
    onError: (error) => {
      toast.error(writeFailureMessage(error, 'We couldn’t remove that tag. Please try again.'));
    },
  });

  return (
    <Card testId={`${PREFIX}-tags-card`}>
      <CardHeader>
        <span className="font-w600 text-text-strong">Tags</span>
      </CardHeader>
      <CardBody>
        <div className="flex flex-col gap-12">
          <div className="flex flex-wrap items-center gap-6">
            {tags.length === 0 ? (
              <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
                No tags yet.
              </span>
            ) : (
              tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-2">
                  <Badge tone="neutral" size="md">
                    {tag}
                  </Badge>
                  <IconButton
                    aria-label={`Remove tag ${tag}`}
                    tone="ghost"
                    size="sm"
                    disabled={removeTag.isPending}
                    onClick={() => removeTag.mutate(tag)}
                    testId={`donor-tag-remove-${tag}`}
                  >
                    <Icon icon={X} size={14} aria-hidden />
                  </IconButton>
                </span>
              ))
            )}
          </div>

          <form
            className="flex items-end gap-8"
            onSubmit={(event) => {
              event.preventDefault();
              const value = label.trim();
              if (value.length === 0 || addTag.isPending) return;
              addTag.mutate(value);
            }}
          >
            <TextField
              label="Add a tag"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Major gift"
              autoComplete="off"
              className="w-[240px]"
              testId="donor-tag-add"
            />
            <Button
              appearance="saas"
              tone="secondary"
              size="md"
              type="submit"
              loading={addTag.isPending}
              disabled={label.trim().length === 0}
              testId={`${PREFIX}-tag-submit`}
            >
              Add
            </Button>
          </form>
        </div>
      </CardBody>
    </Card>
  );
}

// ── Notes ─────────────────────────────────────────────────────────────────────────────────────────

function NotesPanel({
  config,
  donorId,
  onChanged,
}: {
  config: CrmModuleConfig;
  donorId: string;
  onChanged: () => void;
}) {
  const [body, setBody] = useState('');

  const createNote = useMutation({
    retry: false,
    mutationFn: (value: string) => createCrmDonorNote(config.orgRef, donorId, { body: value }),
    onSuccess: () => {
      setBody('');
      onChanged();
    },
    onError: (error) => {
      toast.error(writeFailureMessage(error, 'We couldn’t save that note. Please try again.'));
    },
  });

  return (
    <Card testId={`${PREFIX}-notes-card`}>
      <CardHeader>
        <span className="font-w600 text-text-strong">Add a note</span>
      </CardHeader>
      <CardBody>
        <form
          className="flex flex-col gap-12"
          onSubmit={(event) => {
            event.preventDefault();
            const value = body.trim();
            if (value.length === 0 || createNote.isPending) return;
            createNote.mutate(value);
          }}
          {...testIdProp('donor-note-form')}
        >
          <Textarea
            label="Note"
            rows={3}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder={config.vocab.notePlaceholder}
            testId={`${PREFIX}-note-body`}
          />
          <div className="flex justify-end">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              type="submit"
              loading={createNote.isPending}
              disabled={body.trim().length === 0}
              testId={`${PREFIX}-note-submit`}
            >
              Save note
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

// ── Timeline ──────────────────────────────────────────────────────────────────────────────────────

function TimelinePanel({
  config,
  donorId,
  detail,
  onChanged,
}: {
  config: CrmModuleConfig;
  donorId: string;
  detail: CrmDonorDetailOut;
  onChanged: () => void;
}) {
  const entries = buildTimeline(detail.donations, detail.notes);

  return (
    <Card testId={`${PREFIX}-timeline-card`}>
      <CardHeader>
        <div className="flex flex-col">
          <span className="font-w600 text-text-strong">{config.vocab.activityTitle}</span>
          <span className="text-s12 text-text-tertiary">{config.vocab.activityDescription}</span>
        </div>
      </CardHeader>
      <CardBody>
        <div className="flex flex-col gap-12" {...testIdProp('donor-timeline')}>
          {entries.length === 0 ? (
            <EmptyState
              testId={`${PREFIX}-timeline-empty`}
              icon={<Icon icon={MessageSquare} size={20} aria-hidden />}
              title={config.vocab.emptyActivityTitle}
              description={config.vocab.emptyActivityDescription}
            />
          ) : (
            entries.map((entry) =>
              entry.kind === 'donation' ? (
                <div
                  key={`donation-${entry.donation.id}`}
                  className="flex flex-wrap items-center justify-between gap-12 rounded-lg border border-border-subtle bg-surface p-16"
                  {...testIdProp(`${PREFIX}-timeline-donation`)}
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {/* Amounts are the server's own line values — never re-derived here. */}
                      {displayMoney(entry.donation.gross)}
                    </span>
                    <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
                      {displayDate(entry.donation.date_made)}
                      {entry.donation.designation != null
                        ? ` · ${displayText(entry.donation.designation)}`
                        : ''}
                    </span>
                  </div>
                  {/* Status is selected from the backend's own boolean; the words are never inferred. */}
                  <Badge tone={entry.donation.sent === true ? 'success' : 'neutral'} size="sm">
                    {entry.donation.sent === true ? 'Sent' : 'Preparing'}
                  </Badge>
                </div>
              ) : (
                <NoteRow
                  key={`note-${entry.note.id}`}
                  orgRef={config.orgRef}
                  donorId={donorId}
                  note={entry.note}
                  onChanged={onChanged}
                />
              ),
            )
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function NoteRow({
  orgRef,
  donorId,
  note,
  onChanged,
}: {
  orgRef: string;
  donorId: string;
  note: CrmDonorNoteOut;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);

  const updateNote = useMutation({
    retry: false,
    mutationFn: (value: string) => updateCrmDonorNote(orgRef, donorId, note.id, { body: value }),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
    onError: (error) => {
      toast.error(writeFailureMessage(error, 'We couldn’t update that note. Please try again.'));
    },
  });

  const deleteNote = useMutation({
    retry: false,
    mutationFn: () => deleteCrmDonorNote(orgRef, donorId, note.id),
    onSuccess: onChanged,
    onError: (error) => {
      toast.error(writeFailureMessage(error, 'We couldn’t remove that note. Please try again.'));
    },
  });

  return (
    <div
      className="flex flex-col gap-8 rounded-lg border border-border-subtle bg-surface-sunken p-16"
      {...testIdProp(`${PREFIX}-timeline-note`)}
    >
      <div className="flex flex-wrap items-center justify-between gap-8">
        <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
          {displayText(note.author_name, 'Your team')} · {displayDate(note.created_at)}
        </span>
        <span className="flex items-center gap-4">
          <IconButton
            aria-label="Edit note"
            tone="ghost"
            size="sm"
            disabled={updateNote.isPending}
            onClick={() => {
              setDraft(note.body);
              setEditing((value) => !value);
            }}
            testId="donor-note-edit"
          >
            <Icon icon={Pencil} size={14} aria-hidden />
          </IconButton>
          <IconButton
            aria-label="Remove note"
            tone="ghost"
            size="sm"
            disabled={deleteNote.isPending}
            onClick={() => deleteNote.mutate()}
            testId="donor-note-delete"
          >
            <Icon icon={Trash2} size={14} aria-hidden />
          </IconButton>
        </span>
      </div>

      {editing ? (
        <form
          className="flex flex-col gap-8"
          onSubmit={(event) => {
            event.preventDefault();
            const value = draft.trim();
            if (value.length === 0 || updateNote.isPending) return;
            updateNote.mutate(value);
          }}
        >
          <Textarea
            label="Note"
            rows={3}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            testId={`${PREFIX}-note-edit-body`}
          />
          <div className="flex justify-end gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              type="button"
              onClick={() => setEditing(false)}
              testId="crm-note-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              type="submit"
              loading={updateNote.isPending}
              disabled={draft.trim().length === 0}
              testId={`${PREFIX}-note-edit-save`}
            >
              Save
            </Button>
          </div>
        </form>
      ) : (
        <p className="whitespace-pre-wrap font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
          {note.body}
        </p>
      )}
    </div>
  );
}

// ── Shell ─────────────────────────────────────────────────────────────────────────────────────────

function PageShell({
  children,
  config,
  donorName,
}: {
  children: React.ReactNode;
  config: CrmModuleConfig;
  donorName?: string;
}) {
  return (
    <div
      className="mx-auto flex w-full max-w-[1120px] flex-col gap-16 px-24 py-24"
      {...testIdProp('page-donor-detail')}
    >
      <Link
        to={config.links.contacts}
        activeOptions={{ exact: true }}
        className="inline-flex w-fit items-center gap-6 font-sans text-s13 font-w500 tracking-givmo text-text-tertiary outline-none transition-colors hover:text-text-secondary focus-visible:shadow-ring"
        {...testIdProp(`${PREFIX}-back`)}
      >
        <Icon icon={ArrowLeft} size={14} aria-hidden />
        Back to contacts
      </Link>
      <PageHeader
        title={donorName ?? config.vocab.detailFallbackTitle}
        subtitle={config.vocab.detailSubtitle}
      />
      {children}
    </div>
  );
}
